import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Settings, Users, Activity, FileText, Smartphone, CheckCircle, Eye } from 'lucide-react';
import { whatsappService } from '../../../services/whatsappService';
import IntegrationStatus from './components/IntegrationStatus';
import MessageTemplates from './components/MessageTemplates';
import MessageCenter from './components/MessageCenter';
import BulkMessaging from './components/BulkMessaging';
import Analytics from './components/Analytics';
import DeliveryTracking from './components/DeliveryTracking';


const WhatsAppIntegrationHub = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [integrationSettings, setIntegrationSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [recentMessages, setRecentMessages] = useState([]);

  // Load initial data
  useEffect(() => {
    loadInitialData();
    setupRealtimeSubscriptions();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);

      // Load integration settings
      const { data: settings } = await whatsappService?.getSettings();
      setIntegrationSettings(settings);

      // Load analytics
      const { data: analyticsData } = await whatsappService?.getAnalytics({
        from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)?.toISOString(), // Last 7 days
        to: new Date()?.toISOString()
      });
      setAnalytics(analyticsData);

      // Load recent messages
      const { data: messages } = await whatsappService?.getMessages();
      setRecentMessages(messages?.slice(0, 5) || []);

    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscriptions = () => {
    // Subscribe to new messages
    const messageSubscription = whatsappService?.subscribeToMessages((payload) => {
      if (payload?.eventType === 'INSERT') {
        setRecentMessages(prev => [payload?.new, ...prev?.slice(0, 4)]);
        // Update analytics
        loadAnalytics();
      }
    });

    return () => {
      messageSubscription?.unsubscribe();
    };
  };

  const loadAnalytics = async () => {
    const { data: analyticsData } = await whatsappService?.getAnalytics({
      from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)?.toISOString(),
      to: new Date()?.toISOString()
    });
    setAnalytics(analyticsData);
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Activity },
    { id: 'integration', label: 'Integration Setup', icon: Settings },
    { id: 'templates', label: 'Message Templates', icon: FileText },
    { id: 'messages', label: 'Message Center', icon: MessageCircle },
    { id: 'bulk', label: 'Bulk Messaging', icon: Users },
    { id: 'tracking', label: 'Delivery Tracking', icon: Eye },
    { id: 'analytics', label: 'Analytics', icon: Activity }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-400';
      case 'configured': return 'text-blue-400';
      case 'inactive': return 'text-gray-400';
      case 'error': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getMessageStatusColor = (status) => {
    switch (status) {
      case 'delivered': return 'text-green-400';
      case 'sent': return 'text-blue-400';
      case 'read': return 'text-purple-400';
      case 'failed': return 'text-red-400';
      default: return 'text-yellow-400';
    }
  };

  const renderOverview = () => (
    <div className="space-y-8">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-blue-500/20 text-blue-400">
              <MessageCircle className="w-6 h-6" />
            </div>
            <span className="text-sm text-gray-400">Total Messages</span>
          </div>
          <div className="text-3xl font-bold text-white mb-1">
            {analytics?.total_messages || 0}
          </div>
          <div className="text-sm text-gray-400">Last 7 days</div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-green-500/20 text-green-400">
              <CheckCircle className="w-6 h-6" />
            </div>
            <span className="text-sm text-gray-400">Delivered</span>
          </div>
          <div className="text-3xl font-bold text-white mb-1">
            {analytics?.delivered || 0}
          </div>
          <div className="text-sm text-green-400">
            {analytics?.total_messages ? ((analytics?.delivered / analytics?.total_messages) * 100)?.toFixed(1) : 0}% success rate
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-purple-500/20 text-purple-400">
              <Eye className="w-6 h-6" />
            </div>
            <span className="text-sm text-gray-400">Read Messages</span>
          </div>
          <div className="text-3xl font-bold text-white mb-1">
            {analytics?.read || 0}
          </div>
          <div className="text-sm text-purple-400">
            {analytics?.delivered ? ((analytics?.read / analytics?.delivered) * 100)?.toFixed(1) : 0}% read rate
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between mb-4">
            <div className={`p-3 rounded-xl ${integrationSettings?.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
              <Smartphone className="w-6 h-6" />
            </div>
            <span className="text-sm text-gray-400">Integration Status</span>
          </div>
          <div className={`text-lg font-semibold mb-1 ${getStatusColor(integrationSettings?.status)}`}>
            {integrationSettings?.status?.toUpperCase() || 'NOT CONFIGURED'}
          </div>
          <div className="text-sm text-gray-400">WhatsApp Business API</div>
        </motion.div>
      </div>

      {/* Recent Messages */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold text-white">Recent Messages</h3>
          <button
            onClick={() => setActiveTab('messages')}
            className="text-blue-400 hover:text-blue-300 transition-colors text-sm"
          >
            View All
          </button>
        </div>

        <div className="space-y-4">
          {recentMessages?.length > 0 ? (
            recentMessages?.map((message) => (
              <div key={message?.id} className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                    {message?.recipient?.full_name?.charAt(0) || 'U'}
                  </div>
                  <div>
                    <div className="font-medium text-white">
                      {message?.recipient?.full_name || 'Unknown User'}
                    </div>
                    <div className="text-sm text-gray-400 truncate max-w-xs">
                      {message?.message_content}
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-sm font-medium ${getMessageStatusColor(message?.status)}`}>
                    {message?.status?.toUpperCase()}
                  </div>
                  <div className="text-xs text-gray-500">
                    {new Date(message.created_at)?.toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-400">
              <MessageCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No recent messages</p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="w-16 h-16 border-4 border-blue-400/30 border-t-blue-400 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white">Loading WhatsApp Integration Hub...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/10 backdrop-blur-md border-b border-white/20 sticky top-0 z-40"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <div className="p-2 rounded-xl bg-green-500/20 text-green-400">
                <MessageCircle className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">WhatsApp Integration Hub</h1>
                <p className="text-sm text-gray-400">Seamless communication management</p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <div className={`px-3 py-1 rounded-full text-xs font-medium ${integrationSettings?.status === 'active' ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                }`}>
                {integrationSettings?.status?.toUpperCase() || 'NOT CONFIGURED'}
              </div>
            </div>
          </div>
        </div>
      </motion.header>
      {/* Navigation Tabs */}
      <div className="bg-white/5 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8 overflow-x-auto scrollbar-hide">
            {tabs?.map((tab) => {
              const Icon = tab?.icon;
              return (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 transition-colors whitespace-nowrap ${activeTab === tab?.id
                    ? 'border-blue-400 text-blue-400' : 'border-transparent text-gray-400 hover:text-white hover:border-gray-300'
                    }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{tab?.label}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'integration' && (
              <IntegrationStatus
                settings={integrationSettings}
                onSettingsUpdate={setIntegrationSettings}
              />
            )}
            {activeTab === 'templates' && <MessageTemplates />}
            {activeTab === 'messages' && <MessageCenter />}
            {activeTab === 'bulk' && <BulkMessaging />}
            {activeTab === 'tracking' && <DeliveryTracking />}
            {activeTab === 'analytics' && <Analytics />}
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
};

export default WhatsAppIntegrationHub;